import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCreatePeriodicOperationUtils } from '@/modules/periodicTasks/components/Create/logic';
import ReviewCard from '@/modules/periodicTasks/components/Create/Steps/Review/ReviewCard';
import { PeriodicOperationCategory } from '@/modules/periodicTasks/components/Details/logic';
import { IPeriodicTasks } from '@/modules/periodicTasks/types';
import { useGetPropertyConfigurations } from '@/modules/settings/property/apiHooks';
import { Table } from '@/shared/components/atoms';
import IconWithTitleDescription from '@/shared/components/atoms/IconWithTItleDescription';
import { useStore } from '@/shared/hooks/context/createGenericContext';

const Review: React.FC<{ setStep: Dispatch<SetStateAction<number>> }> = ({
  setStep,
}) => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    getFieldsTableColumns,
    getProductTableColumns,
    getGeneralcardItems,
    getWaterAmountCardItems,
    sharedCardProps,
    getProductsTableData,
  } = useCreatePeriodicOperationUtils();

  const { data: configurations } = useGetPropertyConfigurations(
    selectedAsset?.id
  );

  const { getValues } = useFormContext<IPeriodicTasks.IOperationFormSchema>();
  const formValues = getValues();

  const isIrrigation =
    formValues.operationType.id === PeriodicOperationCategory.Irrigation;

  return (
    <div className='flex flex-col gap-24'>
      <ReviewCard header={t('general-information')} onClick={() => setStep(1)}>
        {getGeneralcardItems(formValues).map((item) => (
          <IconWithTitleDescription
            key={item.title}
            item={{ ...item, ...sharedCardProps }}
          />
        ))}
      </ReviewCard>

      <ReviewCard
        header={t('fields')}
        onClick={() => setStep(2)}
        px='px-0'
        py='py-0'
        cols='grid-cols-1'
      >
        <Table<IPeriodicTasks.IOperationPlotFormValues>
          pagination={false}
          selection={false}
          data={formValues.plots}
          columns={getFieldsTableColumns(configurations?.areaUnit)}
          footerClassName='bg-neutralVariant-95'
          headCellClassName='text-start border-b-xs border-neutralVariant-90 text-surface-10 font-semi-bold'
          rowCellClassName='border-b-xs border-neutralVariant-90'
          headContainerClassName='!bg-background-neutral-99'
          tableWrapperClassName='!rounded-xl'
        />
      </ReviewCard>
      <ReviewCard
        header={t(isIrrigation ? 'water-amount' : 'inputs-used')}
        onClick={() => setStep(3)}
        {...(!isIrrigation
          ? {
              px: 'px-0',
              py: 'py-0',
              cols: 'grid-cols-1',
            }
          : {})}
      >
        {isIrrigation ? (
          getWaterAmountCardItems(formValues, configurations?.areaUnit).map(
            (item) => (
              <IconWithTitleDescription
                key={item.title}
                item={{ ...item, ...sharedCardProps }}
              />
            )
          )
        ) : (
          <Table<IPeriodicTasks.IOperationProductsTableColumn>
            pagination={false}
            selection={false}
            data={getProductsTableData(formValues.products, formValues.plots)}
            columns={getProductTableColumns(
              configurations?.areaUnit,
              formValues.operationType.id
            )}
            footerClassName='bg-neutralVariant-95'
            headCellClassName='text-start border-b-xs border-neutralVariant-90 text-surface-10 font-semi-bold'
            rowCellClassName='border-b-xs border-neutralVariant-90'
            headContainerClassName='!bg-background-neutral-99'
            tableWrapperClassName='!rounded-xl'
          />
        )}
      </ReviewCard>
    </div>
  );
};

export default Review;
